import { zodParse } from 'base/zod_parse';
import { computed, makeObservable, observable } from 'mobx';
import { z } from 'zod';
import {
  AnyDomainConfig,
  Connection,
  Domain,
  GetterReturnOf,
  MutableOf,
  StateOf,
} from './base';
import { MIN_TIMESAMP } from './clock';
import { DeltaType, StringMapDelta } from './deltas';

type StringMapConfig<I extends AnyDomainConfig> = {
  mutableBase: {
    set(key: string, value: StateOf<I>): void;
    delete(key: string): boolean;
    clear(): void;

    get(key: string): GetterReturnOf<I> | undefined;
    getMutable(key: string): MutableOf<I> | undefined;
    has(key: string): boolean;
    readonly size: number;
    entries(): IterableIterator<[string, GetterReturnOf<I>]>;
    keys(): IterableIterator<string>;
    values(): IterableIterator<GetterReturnOf<I>>;
  };
  state: ReadonlyRecordAndMap<StateOf<I>>;
  delta: StringMapDelta<StateOf<I>>;
};

type ReadonlyRecordAndMap<T> = ReadonlyMap<string, T> & {
  readonly __isReadonlyRecordAndMap: true;
};

/**
 * Attaches the map's values as properties, so that it can be JSON serialized.
 *
 * I.e. it is both a record (for JSON) and a map (for JS use).
 **/
export function recordMap<T>(
  input?: ReadonlyMap<string, T> | readonly [string, T][],
) {
  const ret = new Map(input);
  for (const [key, value] of ret.entries()) {
    (ret as any)['~' + key] = value;
  }
  // keep this small, since it gets included in JSON.stringify
  (ret as any).map = 1;
  return ret as any as ReadonlyRecordAndMap<T>;
}

/**
 * String map.
 *
 * A map with string keys, where each value is a domain.
 **/
export class StringMapDomain<I extends AnyDomainConfig>
  implements Domain<StringMapConfig<I>>
{
  constructor(readonly getInnerDomain: (() => Domain<I>) | Domain<I>) {}

  private get innerDomain(): Domain<I> {
    if (this.getInnerDomain instanceof Function) {
      return this.getInnerDomain();
    }
    return this.getInnerDomain;
  }

  create(
    ...args: Parameters<Domain<StringMapConfig<I>>['create']>
  ): MutableOf<StringMapConfig<I>> {
    return new StringMapMutable(...args, this.innerDomain);
  }

  getter(m: MutableOf<StringMapConfig<I>>) {
    return m;
  }

  statesEqual(a: StateOf<StringMapConfig<I>>, b: StateOf<StringMapConfig<I>>) {
    if (a.size !== b.size) return false;
    for (const [key, value] of a) {
      if (!b.has(key)) return false;
      if (!this.innerDomain.statesEqual(value, b.get(key)!)) return false;
    }
    return true;
  }

  readonly stateSchema = createStringMapStateSchema(
    this.innerDomain.stateSchema,
  );

  isMutable(m: unknown): m is MutableOf<StringMapConfig<I>> {
    return m instanceof StringMapMutable && m.innerDomain === this.innerDomain;
  }
}

export function createStringMapStateSchema<V extends z.Schema<any>>(
  valueType: V,
) {
  return z
    .union([z.map(z.string(), valueType), z.record(z.unknown())])
    .transform((o) => {
      if (o instanceof Map) return recordMap(o);
      const ret = new Map();
      let hasMap = false;
      for (const [k, v] of Object.entries(o)) {
        if (k === 'map') {
          hasMap = true;
          continue;
        }
        if (typeof k !== 'string') {
          throw new Error(`Expected string key, got ${k}`);
        }
        if (!k.startsWith('~')) {
          throw new Error(`Expected map key to start with ~, got ${k}`);
        }
        ret.set(k.substring(1), zodParse(valueType, v));
      }
      if (!hasMap) {
        // This should error,
        // but I forgot to add the '.map = 1' marker property in the first version
        // which was released for about an hour.
        // I should migrate the projects in the db.
        console.warn(
          'String map got object without map property set, something is not quite right',
          o,
        );
      }
      return recordMap(ret);
    })
    .default(new Map());
}

class StringMapMutable<I extends AnyDomainConfig>
  implements MutableOf<StringMapConfig<I>>
{
  private readonly mutables: Map<string, MutableOf<I>> = observable.map();
  private readonly latestTime: Map<string, string> = new Map();

  readonly mutable = true;

  constructor(
    readonly oid: string,
    initialState: StateOf<StringMapConfig<I>>,
    private readonly cxn: Connection,
    readonly innerDomain: Domain<I>,
  ) {
    for (const [k, v] of initialState.entries()) {
      this.mutables.set(k, this.innerDomain.create(`${oid}.${k}`, v, cxn));
    }

    makeObservable(this, {
      state: computed,
      visibleOids: computed,
    });

    cxn.register(this, (d) => {
      if (d.type !== DeltaType.MAP_SET) {
        throw new Error(`Unexpected delta ${d.type} for map`);
      }
      const latestTime = this.latestTime.get(d.key) || MIN_TIMESAMP;
      if (d.time <= latestTime) {
        return; // outdated
      }
      this.latestTime.set(d.key, d.time);
      if (d.to == null) {
        this.mutables.delete(d.key);
      } else {
        // The time is used as the id.
        //   (We don't do this initially otherwise all items would have the same id)
        const mutable = this.innerDomain.create(
          d.time,
          zodParse(this.innerDomain.stateSchema, d.to),
          cxn,
        );
        this.mutables.set(d.key, mutable);
      }
    });
  }

  get state(): StateOf<StringMapConfig<I>> {
    const state = new Map<string, StateOf<I>>();
    for (const [k, v] of this.mutables.entries()) {
      state.set(k, v.state);
    }
    return recordMap(state);
  }

  get visibleOids(): string[] {
    const oids: string[] = [];
    for (const v of this.mutables.values()) {
      oids.push(...v.visibleOids);
    }
    return oids;
  }

  wouldChange(delta: StringMapDelta<StateOf<I>>): boolean {
    const latestTime = this.latestTime.get(delta.key) || MIN_TIMESAMP;
    if (delta.time <= latestTime) return false;
    const current = this.mutables.get(delta.key)?.state;
    if (delta.to == null && current == null) return false;
    if (delta.to == null || current == null) return true;
    return !this.innerDomain.statesEqual(current, delta.to);
  }

  get size(): number {
    return this.mutables.size;
  }

  get(key: string): GetterReturnOf<I> | undefined {
    const v = this.mutables.get(key);
    if (v == null) return undefined;
    return this.innerDomain.getter(v);
  }

  getMutable(key: string): MutableOf<I> | undefined {
    return this.mutables.get(key);
  }

  has(key: string): boolean {
    return this.mutables.has(key);
  }

  set(key: string, value: StateOf<I> | undefined): void {
    const existingMut = this.mutables.get(key);
    if (
      existingMut &&
      value &&
      this.innerDomain.statesEqual(existingMut.state, value)
    ) {
      return;
    }
    this.cxn.fromMutable({
      for: this.oid,
      time: this.cxn.clock.advance(),
      type: DeltaType.MAP_SET,
      key,
      to: value,
      from: this.mutables.get(key)?.state,
    } satisfies StringMapDelta<StateOf<I>>);
  }

  delete(key: string): boolean {
    if (!this.mutables.has(key)) {
      return false;
    }
    this.set(key, undefined);
    return true;
  }

  clear(): void {
    for (const key of this.mutables.keys()) {
      this.delete(key);
    }
  }

  *entries(): IterableIterator<[string, GetterReturnOf<I>]> {
    for (const [key, value] of this.mutables.entries()) {
      yield [key, this.innerDomain.getter(value)];
    }
  }

  *keys(): IterableIterator<string> {
    yield* this.mutables.keys();
  }

  *values(): IterableIterator<GetterReturnOf<I>> {
    for (const value of this.mutables.values()) {
      yield this.innerDomain.getter(value);
    }
  }
}
